import toPairs from 'lodash/toPairs';
import { types as sdkTypes } from './sdkLoader';
import { diffInTime } from './dates';
import { extractYouTubeID } from './string';
import { ALLOWED_STATES, getAllowedStatesString } from '../config/allowedZipcodes';

const { LatLng, Money } = sdkTypes;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

/**
 * Validates that a string is unique in an array of strings.
 * @param {Array<string>} stringArray - Array of strings to check against.
 * @param {Function} getMessage - Function that returns an error message.
 * @param {Function} toSlug - Function that converts a string to a slug.
 * @returns {string} - VALID if the string is unique, otherwise the error message.
 */
export const uniqueString = (currentIndex, stringArray, getMessage, toSlug) => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return getMessage('', '');
  }
  const slug = toSlug(value);
  const otherSlugs = stringArray.map(toSlug).filter((_, i) => i !== currentIndex);
  const isUnique = !otherSlugs.includes(slug);
  return isUnique ? VALID : getMessage(value, slug);
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = toPairs(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const requiredSelectTreeOption = message => value => {
  if (typeof value === 'undefined' || value === null || Object.values(value)?.length === 0) {
    return message;
  }
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address &&
    value.selectedPlace.origin instanceof LatLng;
  return selectedPlaceIsValid ? VALID : message;
};

/**
 * Validates that the selected location has a zipcode in an allowed state/country
 * Works with LocationAutocompleteInput value format
 * Supports both US 5-digit zipcodes and Romanian 6-digit postal codes
 * @param {string} message - Error message to display if validation fails
 * @returns {Function} Validator function for Final Form
 */
export const locationZipcodeAllowed = message => value => {
  if (!value || !value.selectedPlace || !value.selectedPlace.address) {
    return VALID; // Let other validators handle missing selection
  }

  const address = value.selectedPlace.address;
  
  // Extract zipcode from address string using regex
  // Matches US 5-digit (12345 or 12345-6789) or Romanian 6-digit (123456)
  const zipcodeMatch = address.match(/\b(\d{5,6})(?:-\d{4})?\b/);
  
  if (!zipcodeMatch) {
    // No postal code in address — fall back to checking country/state name
    const addressLower = address.toLowerCase();
    const isAllowedByName = Object.values(ALLOWED_STATES).some(state => {
      const nameLower = state.name.toLowerCase();
      const abbr = state.abbreviation.toLowerCase();
      // Match country name (e.g. "romania") or US state name/abbreviation (e.g. "florida", ", fl ")
      return (
        addressLower.includes(nameLower) ||
        new RegExp(`(,\\s*${abbr}[^a-z]|\\b${abbr}\\b)`, 'i').test(address)
      );
    });
    if (!isAllowedByName) {
      return message;
    }
    return VALID;
  }

  const zipcode = zipcodeMatch[1];
  const isUSFormat = zipcode.length === 5;
  const isROFormat = zipcode.length === 6;

  // Check against allowed states/countries
  const isAllowed = Object.values(ALLOWED_STATES).some(state => {
    if (state.format === 'US' && isUSFormat) {
      // US zipcodes: check first 3 digits
      const prefix = parseInt(zipcode.substring(0, 3), 10);
      return state.ranges.some(range => prefix >= range.min && prefix <= range.max);
    } else if (state.format === 'RO' && isROFormat) {
      // Romanian postal codes: check first 2-3 digits
      const prefix = parseInt(zipcode.substring(0, 3), 10);
      return state.ranges.some(range => prefix >= range.min && prefix <= range.max);
    }
    return false;
  });

  return isAllowed ? VALID : message;
};

/**
 * Validates that a US zipcode is in an allowed state (Florida or California)
 * @param {string} message - Error message to display if validation fails
 * @returns {Function} Validator function for Final Form
 */
export const zipcodeInAllowedState = message => value => {
  if (!value || typeof value !== 'string') {
    return VALID; // Let required validator handle empty values
  }

  // Extract 5-digit zipcode (handle ZIP+4 format like "12345-6789")
  const zipcode = value.trim().split('-')[0];
  
  // Must be 5 digits
  if (!/^\d{5}$/.test(zipcode)) {
    return VALID; // Let other validators handle format validation
  }

  // Get first 3 digits (zipcode prefix)
  const prefix = parseInt(zipcode.substring(0, 3), 10);

  // Check if prefix falls within any allowed state range
  const isAllowed = Object.values(ALLOWED_STATES).some(state =>
    state.ranges.some(range => prefix >= range.min && prefix <= range.max)
  );

  return isAllowed ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateIsValid = value && value.date instanceof Date;
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

/**
 * Validates booking dates with pickup/return times to ensure:
 * 1. Return time is after pickup time
 * 2. Minimum rental duration is met (at least 1 hour)
 * 3. No backward time travel (booking must be in the future)
 */
export const bookingDatesWithTimesValid = (message, pickupTime, returnTime) => value => {
  const startDate = value?.startDate;
  const endDate = value?.endDate;
  
  if (!startDate || !endDate || !pickupTime || !returnTime) {
    // If times aren't provided, skip this validation (dates-only booking)
    return VALID;
  }

  // Parse times (format: "HH:MM")
  const [pickupHour, pickupMinute] = pickupTime.split(':').map(Number);
  const [returnHour, returnMinute] = returnTime.split(':').map(Number);

  // Create datetime objects
  const pickupDateTime = new Date(startDate);
  pickupDateTime.setHours(pickupHour, pickupMinute, 0, 0);

  const returnDateTime = new Date(endDate);
  returnDateTime.setHours(returnHour, returnMinute, 0, 0);

  // Check if return is after pickup (minimum 1 hour difference)
  const durationInMs = returnDateTime.getTime() - pickupDateTime.getTime();
  const oneHourInMs = 60 * 60 * 1000;

  if (durationInMs < oneHourInMs) {
    return message || 'Return time must be at least 1 hour after pickup time';
  }

  // Check if booking is in the future (allow bookings starting now or later)
  const now = new Date();
  if (pickupDateTime.getTime() < now.getTime()) {
    return 'Pickup time cannot be in the past';
  }

  return VALID;
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

export const moneySubUnitAmountAtMost = (message, maxValue) => value => {
  return value instanceof Money && value.amount <= maxValue ? VALID : message;
};

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const numberAtLeast = (message, minNumber) => value => {
  const valueNum = parseNum(value);
  return typeof valueNum === 'number' && valueNum >= minNumber ? VALID : message;
};

export const validateInteger = (value, max, min, numberTooSmallMessage, numberTooBigMessage) => {
  const parsedValue = Number.parseInt(value, 10);
  if (parsedValue > max) {
    return numberTooBigMessage;
  }
  if (parsedValue < min) {
    return numberTooSmallMessage;
  }
  return VALID;
};

// If URL is passed to this function as null, will return VALID
export const validateYoutubeURL = (url, message) => {
  return url ? (extractYouTubeID(url) ? VALID : message) : VALID;
};

export const ageAtLeast = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = new Date();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = diffInTime(now, age, 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const validBusinessURL = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  }

  const disallowedChars = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/;
  const protocolTokens = value.split(':');
  const includesProtocol = protocolTokens.length > 1;
  const usesHttpProtocol = includesProtocol && !!protocolTokens[0].match(/^(https?)/);

  const invalidCharacters = !!value.match(disallowedChars);
  const invalidProtocol = !(usesHttpProtocol || !includesProtocol);
  // Stripe checks against example.com
  const isExampleDotCom = !!value.match(/^(https?:\/\/example\.com|example\.com)/);
  const isLocalhost = !!value.match(/^(https?:\/\/localhost($|:|\/)|localhost($|:|\/))/);
  return invalidCharacters || invalidProtocol || isExampleDotCom || isLocalhost ? message : VALID;
};

export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const validSGID = message => value => {
  return value.length === 9 ? VALID : message;
};

export const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), VALID);
